import { createContext, useContext, useEffect } from "react";
import type { ReactNode } from "react";
import { Link, type LinkProps, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANG, type Lang } from "./types";

const LangContext = createContext<Lang>(DEFAULT_LANG);

export const useLang = (): Lang => useContext(LangContext);

/** Split "/path#hash" into ["/path", "#hash"]. */
function splitHash(to: string): [string, string] {
  const i = to.indexOf("#");
  if (i === -1) return [to, ""];
  return [to.slice(0, i), to.slice(i)];
}

/** Prefix an internal path with the language segment (no prefix for the default language). */
export function localizePath(to: string, lang: Lang): string {
  const [path, hash] = splitHash(to);
  if (lang === DEFAULT_LANG) return path + hash;
  let np = path;
  if (path === "" || path === "/") np = `/${lang}`;
  else if (path.startsWith("/")) np = `/${lang}${path}`;
  // a bare "#hash" (path === "") stays as-is — same-page anchor
  return np + hash;
}

/** Layout that pins the active language for everything below it. */
export function LangLayout({ lang }: { lang: Lang }) {
  const { i18n } = useTranslation();

  // Keep i18next + <html lang> in sync. Dictionary access does not depend on
  // this (it reads LangContext), so first paint is already correct.
  if (i18n.language !== lang) i18n.changeLanguage(lang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LangContext.Provider value={lang}>
      <Outlet />
    </LangContext.Provider>
  );
}

/** Language-aware <Link>. `to` is always written as the default-language path. */
export function L({ to, children, ...rest }: Omit<LinkProps, "to"> & { to: string; children: ReactNode }) {
  const lang = useLang();
  return (
    <Link to={localizePath(to, lang)} {...rest}>
      {children}
    </Link>
  );
}
